import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { wafApi } from '../../services/api/waf.api'
import { useAuthStore } from '../../store/authStore'

// No automatic retry anywhere below: 401/403/404 are real answers (a Free-plan
// user will see 403 here), and a retry only delays the screen that explains it.

/**
 * The paginated assessments list (max 10 per page — a server cap). Reads the URL
 * for filters, ordering and page; `latest` defaults to `true` (each endpoint's
 * newest assessment) and `latest=false` is the full history.
 */
export function useWaf() {
  const orgId = useAuthStore((s) => s.orgId)
  const [searchParams] = useSearchParams()
  const next = new URLSearchParams(searchParams)
  if (!next.has('latest')) next.set('latest', 'true')
  const params = next.toString()
  return useQuery({
    queryKey: queryKeys.waf.list(orgId, params),
    queryFn: () => wafApi.getList(params),
    enabled: !!orgId,
    retry: false,
  })
}

/** Facet options, computed over ALL of the organization's assessments. */
export function useWafFilterOptions() {
  const orgId = useAuthStore((s) => s.orgId)
  return useQuery({
    queryKey: queryKeys.waf.filterOptions(orgId),
    queryFn: () => wafApi.getFilterOptions(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
    retry: false,
  })
}

/** One assessment and its per-attack-class breakdown. */
export function useWafDetail(id: string | null) {
  const orgId = useAuthStore((s) => s.orgId)
  return useQuery({
    queryKey: queryKeys.waf.detail(orgId, id || ''),
    queryFn: () => wafApi.getById(id!),
    enabled: !!id && !!orgId,
    retry: false,
  })
}
